import { UserImportStatus } from '@dadohr/shared/domainTypes/user';
import { assertNever } from 'assert-never';
import { useMemo } from 'react';
import { Color, ColoredChip } from '../../../../../components/ColoredChip';
import { TFunction, useTranslation } from '../../../../../i18next';

const getConfig = (
  status: UserImportStatus,
  outdated: boolean,
  t: TFunction
): [string, Color] => {
  if (outdated) {
    return [t('import.status.outdated'), 'gray'];
  }
  switch (status) {
    case UserImportStatus.PENDING:
      return [t('import.status.pending'), 'yellow'];
    case UserImportStatus.PREVIEW:
      return [t('import.status.preview'), 'blue'];
    case UserImportStatus.COMMITTED:
      return [t('import.status.committed'), 'green'];
    case UserImportStatus.DONE:
      return [t('import.status.done'), 'green'];
    case UserImportStatus.ERROR:
      return [t('import.status.error'), 'red'];
    case UserImportStatus.PARTIAL_FAILURE:
      // Treat partial-failure as a regular success here — the per-user
      // rejections are surfaced in the Problems tab and the count is folded
      // into stats.issues at render time. Showing the import itself as red
      // implies the whole batch failed, which isn't accurate.
      return [t('import.status.done'), 'green'];
    default:
      return assertNever(status);
  }
};

export const ImportStatusChip = ({
  status,
  outdated = false,
  size
}: {
  status: UserImportStatus;
  outdated?: boolean;
  size?: 'small' | 'medium';
}) => {
  const { t } = useTranslation();

  const [label, color] = useMemo(() => getConfig(status, outdated, t), [
    status,
    t,
    outdated
  ]);
  return (
    <ColoredChip color={color} size={size}>
      {label}
    </ColoredChip>
  );
};
